import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import { providerProfileApi, ApiError } from '../api';
import type { ProviderService, ServiceStatus, ServiceRequest } from '../types/provider';

export interface ServiceVariant {
	id?: string;
	/** Both are optional on the API. An empty string is "not specified". */
	size: string;
	length: string;
	duration: number;
	price: number;
}

export interface Service {
	id: string;
	name: string;
	description: string;
	status: ServiceStatus;
	variants: ServiceVariant[];
	/**
	 * Category name, for display only. The backend keeps it in step with the
	 * category it belongs to; `categoryId` is the field that does the filing.
	 */
	category?: string | null;
	/** null / undefined means uncategorized. */
	categoryId?: number | null;
	/**
	 * Offered as an optional extra after the main service on the paid booking
	 * link, rather than as something a customer books on its own.
	 */
	addOn: boolean;
}

/**
 * Map API response → UI model. Shared with the staff services store: both
 * portals read and write the same service entity, only the endpoints differ.
 * `description` comes back null for services saved without one, and the editor
 * calls `.trim()` on it, so it is coerced to '' here — as are `size` and
 * `length`, which are null on combinations saved without them.
 */
export const mapApiService = (apiService: ProviderService): Service => ({
	id: String(apiService.id),
	name: apiService.serviceName ?? '',
	description: apiService.description ?? '',
	status: apiService.status,
	category: apiService.category ?? null,
	categoryId: apiService.categoryId ?? null,
	addOn: apiService.addOn ?? false,
	variants: (apiService.pricingCombinations ?? []).map((combo, idx) => ({
		id: `${apiService.id}-${idx}`,
		size: combo.size ?? '',
		length: combo.length ?? '',
		duration: combo.durationMinutes,
		price: combo.price,
	})),
});

/**
 * Map UI model → API request body. `category` and `categoryId` are both sent:
 * the editor sets them together, and anything it doesn't touch is carried
 * through from the loaded service rather than being blanked on save. A size or
 * length left unset goes back as null rather than '' — the API treats those
 * fields as absent, not as an empty value.
 */
export const toServiceRequest = (service: Service): ServiceRequest => ({
	serviceName: service.name,
	description: service.description,
	category: service.category ?? null,
	categoryId: service.categoryId ?? null,
	addOn: service.addOn,
	pricingCombinations: service.variants.map(v => ({
		size: v.size || null,
		length: v.length || null,
		durationMinutes: v.duration,
		price: v.price,
	})),
	status: service.status,
});

interface ServicesState {
	services: Service[];
	isLoading: boolean;
	error: string | null;
	fetchServices: (locationId: number) => Promise<void>;
	addService: (locationId: number, service: Service) => Promise<void>;
	updateService: (locationId: number, id: string, service: Service) => Promise<void>;
	deleteService: (locationId: number, id: string) => Promise<void>;
	clearServices: () => void;
}

const useServicesStore = create<ServicesState>()(
	devtools(
		set => ({
			services: [],
			isLoading: false,
			error: null,

			fetchServices: async (locationId: number) => {
				set({ isLoading: true, error: null });

				try {
					const apiServices = await providerProfileApi.getServices(locationId);
					set({
						services: apiServices.map(mapApiService),
						isLoading: false,
					});
				} catch (error) {
					const apiError = error as ApiError;
					set({
						error: apiError.message || 'Failed to load services',
						isLoading: false,
					});

					if (import.meta.env.DEV) {
						// eslint-disable-next-line no-console
						console.error('Services fetch error:', apiError);
					}
				}
			},

			addService: async (locationId: number, service: Service) => {
				const created = await providerProfileApi.createService(
					locationId,
					toServiceRequest(service)
				);
				set(state => ({
					services: [...state.services, mapApiService(created)],
				}));
			},

			updateService: async (locationId: number, id: string, service: Service) => {
				const updated = await providerProfileApi.updateService(
					Number(id),
					locationId,
					toServiceRequest(service)
				);
				set(state => ({
					services: state.services.map(s => (s.id === id ? mapApiService(updated) : s)),
				}));
			},

			deleteService: async (locationId: number, id: string) => {
				await providerProfileApi.deleteService(Number(id), locationId);
				set(state => ({
					services: state.services.filter(s => s.id !== id),
				}));
			},

			clearServices: () => {
				set({ services: [], isLoading: false, error: null });
			},
		}),
		{ name: 'services-store' }
	)
);

export default useServicesStore;
